import classNames from 'classnames';
import { memo, ReactNode } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { Checkbox, CheckboxGroup } from 'rsuite';
import { Callout, CurrencyInput, InputAddon, InputBox, InputGroup, Layout } from '../../../../../../../components';
import { RegisterLimitFormValues, RegisterLimitSchemaProps } from '../../lib/schema';
import styles from './RegisterLimitPageForm.module.scss';

type RegisterLimitPageFormProps = {
  form: UseFormReturn<RegisterLimitFormValues, unknown, RegisterLimitSchemaProps>;
};

type LimitFieldName = 'minMargin' | 'maxMargin' | 'downwardVariation' | 'upwardVariation' | 'minCpi' | 'maxCpi';

type LimitSectionProps = {
  title: string;
  subtitle: string;
  helper: ReactNode;
  minName: LimitFieldName;
  maxName: LimitFieldName;
  minLabel: string;
  maxLabel: string;
  unit?: string;
  form: UseFormReturn<RegisterLimitFormValues, unknown, RegisterLimitSchemaProps>;
  allowNegative?: boolean;
  selector?: ReactNode;
};

const parsePercentageInput = (value: string | null | undefined) => {
  if (value == null || value.trim() === '') return null;

  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};

type LimitFieldProps = {
  form: UseFormReturn<RegisterLimitFormValues, unknown, RegisterLimitSchemaProps>;
  name: LimitFieldName;
  label: string;
  unit?: string;
  allowNegative?: boolean;
};

const LimitField = ({ form, name, label, unit = '%', allowNegative = false }: LimitFieldProps) => {
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field, fieldState: { error } }) => (
        <InputBox className={styles.field}>
          <InputBox.Label htmlFor={name}>{label}</InputBox.Label>
          <InputGroup className={styles['input-group']}>
            <CurrencyInput
              id={name}
              name={field.name}
              ref={field.ref}
              value={parsePercentageInput(form.getValues(name))}
              className={styles.input}
              skin={error ? 'red' : 'gray'}
              allowNull
              allowNegative={allowNegative}
              precision={1}
              inputMode="decimal"
              type="text"
              aria-label={label}
              onChangeEvent={(_, maskedValue) => field.onChange(maskedValue || null)}
              // onBlur={event => field.onBlur(event)}
              onBlur={() => field.onBlur()}
            />
            <InputAddon>{unit}</InputAddon>
          </InputGroup>
          <InputBox.Error message={error?.message} />
        </InputBox>
      )}
    />
  );
};

const LimitSection = ({ title, subtitle, helper, minName, maxName, minLabel, maxLabel, unit, form, allowNegative, selector }: LimitSectionProps) => (
  <Layout.Section padded className={styles.section}>
    <div className={styles['section-header']}>
      <h5>{title}</h5>
      <p>{subtitle}</p>
    </div>
    {selector}
    <div className={styles.fields}>
      <LimitField form={form} name={minName} label={minLabel} unit={unit} allowNegative={allowNegative} />
      <LimitField form={form} name={maxName} label={maxLabel} unit={unit} allowNegative={allowNegative} />
    </div>
    <Callout className={styles.callout} variant="info" size="sm">
      <Callout.Description>{helper}</Callout.Description>
    </Callout>
  </Layout.Section>
);

const PriceAdjustmentSection = ({ form }: RegisterLimitPageFormProps) => (
  <Layout.Section padded className={styles['adjust-price-section']}>
    <div className={styles['adjust-price-option']}>
      <Controller
        name="adjustPricesAccordingToLimits"
        control={form.control}
        render={({ field: { value, name, onChange, onBlur, ref } }) => (
          <CheckboxGroup name={name} value={value ? [name] : []} onChange={selectedValues => onChange(selectedValues.includes(name))}>
            {[
              <Checkbox key={name} value={name} inputRef={ref} onBlur={onBlur} className={styles['adjust-price-checkbox']}>
                <span className={styles['adjust-price-content']}>
                  <span className={styles['adjust-price-title']}>Ajustar preços de acordo com os limites</span>
                  <span className={styles['adjust-price-description']}>
                    Os preços sugeridos que ficarem fora dos limites serão ajustados. Caso os preços excedam para cima eles serão ajustados pelo % máximo e se
                    os preços excederem para baixo eles serão ajustados pelo % mínimo.
                  </span>
                </span>
              </Checkbox>,
            ]}
          </CheckboxGroup>
        )}
      />
    </div>
  </Layout.Section>
);

type MarginType = 'margin' | 'objectiveMargin';

const MarginTypeSelector = ({ form }: { form: UseFormReturn<RegisterLimitFormValues, unknown, RegisterLimitSchemaProps> }) => (
  <Controller
    name="marginType"
    control={form.control}
    render={({ field }) => {
      const selectedType = field.value ?? 'margin';

      const handleChange = (value: MarginType) => {
        field.onChange(value);

        if (value === 'objectiveMargin') {
          form.setValue('minMargin', null, { shouldDirty: true });
          form.setValue('maxMargin', null, { shouldDirty: true });
          form.clearErrors(['minMargin', 'maxMargin']);
          return;
        }

        form.setValue('downwardVariation', null, { shouldDirty: true });
        form.setValue('upwardVariation', null, { shouldDirty: true });
        form.clearErrors(['downwardVariation', 'upwardVariation']);
      };

      return (
        <InputBox.RadioGroup className={styles['margin-type-radio-group']} value={selectedType} onChange={value => handleChange(value as MarginType)}>
          <InputBox.Radio
            value="margin"
            className={classNames(styles['margin-type-option'], selectedType === 'margin' && styles['margin-type-option-selected'])}
          >
            <span className={styles['margin-type-content']}>
              <span className={styles['margin-type-label']}>Margem</span>
              <span className={styles['margin-type-description']}>Considera a margem definida aqui no limite</span>
            </span>
          </InputBox.Radio>
          <InputBox.Radio
            value="objectiveMargin"
            className={classNames(styles['margin-type-option'], selectedType === 'objectiveMargin' && styles['margin-type-option-selected'])}
          >
            <span className={styles['margin-type-content']}>
              <span className={styles['margin-type-label']}>Margem objetiva</span>
              <span className={styles['margin-type-description']}>O limite considera a margem objetiva definida na sua estratégia</span>
            </span>
          </InputBox.Radio>
        </InputBox.RadioGroup>
      );
    }}
  />
);

const RegisterLimitPageForm = memo(({ form }: RegisterLimitPageFormProps) => {
  const marginType = useWatch({ control: form.control, name: 'marginType' }) ?? 'margin';
  const isObjectiveMargin = marginType === 'objectiveMargin';

  return (
    <div className={styles.form}>
      <LimitSection
        form={form}
        title="Limites de margem"
        subtitle="Escolha o tipo de margem e defina a variação mínima e máxima."
        selector={<MarginTypeSelector form={form} />}
        helper={
          isObjectiveMargin ? (
            <>
              Crie um <strong className={styles['helper-emphasis']}>limite de margem</strong> para cada regra de competitividade, pois ele garante que caso haja
              variação agressiva de preço concorrência, suas margens sejam preservadas.
            </>
          ) : (
            <>
              Crie um <strong className={styles['helper-emphasis']}>limite de margem</strong> para cada regra baseada em competitividade, assim você garante que
              a estratégia não comprometa a sua rentabilidade.
            </>
          )
        }
        minName={isObjectiveMargin ? 'downwardVariation' : 'minMargin'}
        maxName={isObjectiveMargin ? 'upwardVariation' : 'maxMargin'}
        minLabel={isObjectiveMargin ? 'Variação para baixo' : '% Mínimo'}
        maxLabel={isObjectiveMargin ? 'Variação para cima' : '% Máximo'}
        unit={isObjectiveMargin ? 'p.p.' : '%'}
        allowNegative={!isObjectiveMargin}
      />
      <LimitSection
        form={form}
        title="Limites de competitividade"
        subtitle="Defina a variação mínima e máxima da competitividade."
        helper={
          <>
            Crie um <strong className={styles['helper-emphasis']}>limite de competitividade</strong> para cada regra baseada em margem, assim será possível
            verificar se sua estratégia não está muito distante da realidade do mercado.
          </>
        }
        minName="minCpi"
        maxName="maxCpi"
        minLabel="% Mínimo"
        maxLabel="% Máximo"
      />
      <PriceAdjustmentSection form={form} />
    </div>
  );
});

RegisterLimitPageForm.displayName = 'RegisterLimitPageForm';

export default RegisterLimitPageForm;
